import React, { useState, useEffect } from "react";
import { callApiWithOptions, HttpMethod } from "src/services/apiService";
import styles from "./DialerLogs.module.css";

interface Props {
  phoneNumber: string;
}

interface WarmupCallLog {
  warmupCallLogId: number;
  calledOn: string | null;
  callDurationSecs: number | null;
  devicePhoneNumber: string | null;
  carrier: string | null;
  success: boolean | null;
  warmupJobId: number | null;
}

const formatLocal = (d: string | null): string => {
  if (!d) return "\u2014";
  return new Date(d).toLocaleString();
};

const formatPhone = (phone: string | null): string => {
  if (!phone) return "\u2014";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `+1 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  return phone;
};

const WarmupDialerLogs: React.FC<Props> = ({ phoneNumber }) => {
  const [expanded, setExpanded] = useState(false);
  const [logs, setLogs] = useState<WarmupCallLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [expandedLogIdx, setExpandedLogIdx] = useState<number | null>(null);

  useEffect(() => {
    setLogs([]);
    setLoaded(false);
    setError("");
    setExpanded(false);
  }, [phoneNumber]);

  const handleToggle = async () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);

    if (newExpanded && !loaded && phoneNumber) {
      setIsLoading(true);
      setError("");
      try {
        const resp = await callApiWithOptions({
          endpoint: `sysadmin/advanced-remediation/warmup-dialer-logs?phoneNumber=${encodeURIComponent(phoneNumber)}`,
          method: HttpMethod.GET,
        });
        setLogs(resp?.callLogs || []);
        setLoaded(true);
      } catch (err: any) {
        setError(err.message || "Failed to load warmup logs");
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className={styles.section}>
      <div className={styles.header} onClick={handleToggle}>
        <h5 className={styles.title}>Warmup Dialer Logs</h5>
        <span className={styles.chevron}>{expanded ? "\u25B2" : "\u25BC"}</span>
      </div>
      {expanded && (
        <div className={styles.body}>
          {isLoading && (
            <div className="text-center py-3">
              <span className="spinner-border spinner-border-sm me-2" />
              Loading...
            </div>
          )}
          {error && <div className="alert alert-danger mb-0">{error}</div>}
          {loaded && !isLoading && logs.length === 0 && (
            <div className="text-muted py-2">No warmup dialer logs found for this phone number.</div>
          )}
          {loaded && logs.length > 0 && (
            <div className="table-responsive">
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    <th>Log ID</th>
                    <th>Called On</th>
                    <th>Duration</th>
                    <th>Device</th>
                    <th>Carrier</th>
                    <th>Success</th>
                    <th>Job ID</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, idx) => (
                    <React.Fragment key={log.warmupCallLogId ?? idx}>
                      <tr>
                        <td>{log.warmupCallLogId}</td>
                        <td>{formatLocal(log.calledOn)}</td>
                        <td>
                          {log.callDurationSecs != null
                            ? `${log.callDurationSecs}s`
                            : "\u2014"}
                        </td>
                        <td>{formatPhone(log.devicePhoneNumber)}</td>
                        <td>{log.carrier || "\u2014"}</td>
                        <td>
                          {log.success === true ? (
                            <span className="text-success fw-bold">Yes</span>
                          ) : log.success === false ? (
                            <span className="text-danger fw-bold">No</span>
                          ) : (
                            "\u2014"
                          )}
                        </td>
                        <td>{log.warmupJobId ?? "\u2014"}</td>
                        <td>
                          <button
                            className="btn btn-link btn-sm text-muted p-0"
                            style={{ fontSize: "11px" }}
                            onClick={() =>
                              setExpandedLogIdx(expandedLogIdx === idx ? null : idx)
                            }
                          >
                            JSON
                          </button>
                        </td>
                      </tr>
                      {expandedLogIdx === idx && (
                        <tr>
                          <td colSpan={8} className="p-0">
                            <pre className={styles.jsonPre}>
                              {JSON.stringify(log, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WarmupDialerLogs;
